import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { translations, LINKS, getNimnyAppUrl } from '../../data/translations';

// App screenshot shown inside the phone mockup. Single locally-hosted WebP
// (no language fork — the artwork is language-agnostic).
const APP_SCREENSHOT = '/assets/landing/hero-phone-screen.webp';

// App Store badge (realistic style) - same size as Google Play
const AppStoreBadge = () => (
  <svg viewBox="0 0 137 44" className="h-10 opacity-50 hover:opacity-70 transition-opacity">
    <defs>
      <linearGradient id="appstore-bg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1a1a1a"/>
        <stop offset="100%" stopColor="#000"/>
      </linearGradient>
    </defs>
    <rect x="1" y="2" width="135" height="40" rx="6" fill="url(#appstore-bg)" stroke="#a6a6a6" strokeWidth="1"/>
    {/* Apple logo */}
    <path d="M32.5 22.5c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2-1.5 2.6-.4 6.4 1 8.5.7 1 1.5 2.2 2.6 2.1 1-.1 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1 0 1.8-1 2.5-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.2-.8-2.2-3.5zm-2.1-6.4c.6-.7 1-1.7.9-2.6-.8 0-1.9.6-2.5 1.3-.5.6-1 1.7-.9 2.6.9.1 1.9-.5 2.5-1.3z" fill="#fff"/>
    {/* Text */}
    <text x="85" y="16" textAnchor="middle" fill="#fff" fontSize="7" fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif">Download on the</text>
    <text x="85" y="30" textAnchor="middle" fill="#fff" fontSize="14" fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" fontWeight="500">App Store</text>
  </svg>
);

// Google Play badge (realistic style)
const GooglePlayBadge = () => (
  <svg viewBox="0 0 137 44" className="h-10 opacity-50 hover:opacity-70 transition-opacity">
    <defs>
      <linearGradient id="play-bg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1a1a1a"/>
        <stop offset="100%" stopColor="#000"/>
      </linearGradient>
      <linearGradient id="play-icon" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00d4ff"/>
        <stop offset="33%" stopColor="#00f076"/>
        <stop offset="66%" stopColor="#ffbc00"/>
        <stop offset="100%" stopColor="#ff3a44"/>
      </linearGradient>
    </defs>
    <rect x="1" y="2" width="135" height="40" rx="6" fill="url(#play-bg)" stroke="#a6a6a6" strokeWidth="1"/>
    {/* Play triangle */}
    <path d="M18 12l12 10-12 10V12z" fill="url(#play-icon)"/>
    {/* Text */}
    <text x="81" y="16" textAnchor="middle" fill="#a6a6a6" fontSize="7" fontFamily="'Roboto', 'Product Sans', sans-serif" letterSpacing="0.5">GET IT ON</text>
    <text x="81" y="30" textAnchor="middle" fill="#fff" fontSize="14" fontFamily="'Roboto', 'Product Sans', sans-serif" fontWeight="400">Google Play</text>
  </svg>
);

const HeroSectionNew = () => {
  const { language, t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  const trackClick = (buttonName) => {
    if (window.plausible) {
      window.plausible('Click', { props: { button: buttonName } });
    }
  };

  // Get the Create form URL based on language
  const getCreateUrl = () => {
    if (language === 'ru') return 'https://nimny.app/ru/agent';
    if (language === 'es') return 'https://nimny.app/es/agent';
    return 'https://nimny.app/en/agent';
  };

  // Get About page URL based on language
  const getAboutUrl = () => {
    if (language === 'ru') return '/ru/about-nimny';
    if (language === 'es') return '/es/about-nimny';
    return '/about-nimny';
  };

  // Translations for the new hero
  const heroText = {
    title: t(translations.hero.headline),
    subtitle: t(translations.hero.subheadline),
    readStories: t(translations.nav.readOnline),
    createStory: {
      en: 'Create your story',
      es: 'Crea tu cuento',
      ru: 'Создай сказку'
    }[language],
    readAmazon: t(translations.nav.readOnAmazon),
    aboutNimny: {
      en: 'About Nimny',
      es: 'Sobre Nimny',
      ru: 'О Нимни'
    }[language]
  };

  return (
    <section className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden pt-20 pb-8 px-4">
      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col items-center">
        
        {/* Title */}
        <h1 
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#c9a87c] mb-3 md:mb-4 text-center leading-tight"
          style={{ fontFamily: '"Patrick Hand", cursive', letterSpacing: '-0.02em' }}
          data-testid="hero-title"
        >
          {heroText.title}
        </h1>

        {/* Subtitle */}
        <p className="font-serif text-lg md:text-xl lg:text-2xl text-[#F4E6C8] mb-8 md:mb-12 text-center max-w-2xl italic px-2">
          {heroText.subtitle}
        </p>

        {/* Device Mockup - responsive sizing */}
        <div className="relative mb-6 md:mb-12">
          {/* Phone Frame - iPhone 17 Pro (71.9:150) on mobile, iPhone 17 Pro Max (3.07:6.43) on desktop */}
          <div className="relative mx-auto w-[150px] md:w-[220px]">
            {/* Outer glow */}
            <div 
              className="absolute inset-0 rounded-[28px] md:rounded-[44px] opacity-30"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(201,168,124,0.4) 0%, transparent 70%)',
                transform: 'scale(1.3)',
                filter: 'blur(30px)'
              }}
            />
            
            {/* Phone body - mobile: 71.9:150 (iPhone 17 Pro), desktop: 3.07:6.43 (iPhone 17 Pro Max) */}
            <div 
              className="relative bg-[#1a1a2e] rounded-[24px] md:rounded-[40px] p-[4px] md:p-[6px] border border-[#c9a87c]/20 shadow-2xl aspect-[71.9/150] md:aspect-[3.07/6.43]"
            >
              {/* Screen bezel — the screenshot fills the full screen
                  area edge-to-edge; the Dynamic Island and home indicator
                  sit on top of it like on a real iPhone. */}
              <div className="relative bg-[#0f0f1a] rounded-[20px] md:rounded-[34px] overflow-hidden h-full">
                {/* Full-screen artwork */}
                <img
                  src={APP_SCREENSHOT}
                  alt="Nimny App"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                  style={{ animation: 'float 7s ease-in-out infinite' }}
                />

                {/* Dynamic Island (overlay on top of the screen) */}
                <div className="absolute top-1 md:top-2 left-1/2 -translate-x-1/2 z-10">
                  <div className="w-14 md:w-24 h-[14px] md:h-[24px] bg-[#000] rounded-full" />
                </div>

                {/* Home indicator (overlay on top of the screen) */}
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10">
                  <div className="w-20 md:w-28 h-1 bg-[#fff]/40 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Store / Google Play badges. App Store is live; Google Play
            badge is shown but kept inactive until the Android app ships. */}
        <div className="flex flex-col items-center gap-3 mb-10 md:mb-14">
          <div className="flex items-center gap-3">
            <a
              href="https://apps.apple.com/us/app/nimny/id6762245198"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('App Store - Hero')}
              aria-label="Download Nimny on the App Store"
              data-testid="hero-app-store-link"
              className="transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#c9a87c]/60 rounded-md"
            >
              <AppStoreBadge />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.nimny.app"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('Google Play - Hero')}
              aria-label="Get Nimny on Google Play"
              data-testid="hero-google-play-link"
              className="transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#c9a87c]/60 rounded-md"
            >
              <GooglePlayBadge />
            </a>
          </div>
        </div>

        {/* Action Buttons Grid - All buttons same style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 w-full max-w-3xl px-2" data-testid="hero-actions">
          {/* Read Stories */}
          <a 
            href={getNimnyAppUrl(language)}
            target="_blank" 
            rel="noopener noreferrer"
            onClick={() => trackClick('Read Stories - Hero')}
            className="group"
            data-testid="hero-read-stories"
          >
            <div className="flex flex-col items-center justify-center p-4 md:p-6 rounded-2xl bg-[#c9a87c]/15 border border-[#c9a87c]/40 text-[#c9a87c] transition-all duration-300 hover:bg-[#c9a87c]/25 hover:border-[#c9a87c]/60 hover:shadow-[0_0_25px_rgba(201,168,124,0.2)] hover:scale-[1.02] h-[110px] md:h-[120px]">
              <svg className="w-7 h-7 md:w-7 md:h-7 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
              <span className="font-sans text-base md:text-base font-medium text-center">{heroText.readStories}</span>
            </div>
          </a>

          {/* Create Story */}
          <a 
            href={getCreateUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('Create Story - Hero')}
            className="group"
            data-testid="hero-create-story"
          >
            <div className="flex flex-col items-center justify-center p-4 md:p-6 rounded-2xl bg-[#c9a87c]/15 border border-[#c9a87c]/40 text-[#c9a87c] transition-all duration-300 hover:bg-[#c9a87c]/25 hover:border-[#c9a87c]/60 hover:shadow-[0_0_25px_rgba(201,168,124,0.2)] hover:scale-[1.02] h-[110px] md:h-[120px]">
              <svg className="w-7 h-7 md:w-7 md:h-7 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
              <span className="font-sans text-base md:text-base font-medium text-center">{heroText.createStory}</span>
            </div>
          </a>

          {/* Read on Amazon */}
          <a 
            href={LINKS.amazon}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('Read on Amazon - Hero')}
            className="group"
            data-testid="hero-amazon"
          >
            <div className="flex flex-col items-center justify-center p-4 md:p-6 rounded-2xl bg-[#c9a87c]/15 border border-[#c9a87c]/40 text-[#c9a87c] transition-all duration-300 hover:bg-[#c9a87c]/25 hover:border-[#c9a87c]/60 hover:shadow-[0_0_25px_rgba(201,168,124,0.2)] hover:scale-[1.02] h-[110px] md:h-[120px]">
              <svg className="w-7 h-7 md:w-7 md:h-7 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              <span className="font-sans text-base md:text-base font-medium text-center">{heroText.readAmazon}</span>
            </div>
          </a>

          {/* About Nimny */}
          <a 
            href={getAboutUrl()}
            onClick={() => trackClick('About Nimny - Hero')}
            className="group"
            data-testid="hero-about"
          >
            <div className="flex flex-col items-center justify-center p-4 md:p-6 rounded-2xl bg-[#c9a87c]/15 border border-[#c9a87c]/40 text-[#c9a87c] transition-all duration-300 hover:bg-[#c9a87c]/25 hover:border-[#c9a87c]/60 hover:shadow-[0_0_25px_rgba(201,168,124,0.2)] hover:scale-[1.02] h-[110px] md:h-[120px]">
              <svg className="w-7 h-7 md:w-7 md:h-7 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
              <span className="font-sans text-base md:text-base font-medium text-center">{heroText.aboutNimny}</span>
            </div>
          </a>
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.02); }
        }
      `}</style>
    </section>
  );
};

export default HeroSectionNew;
